import { useNavigate, useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import { getLivingBaseUrl, getLivingWidgetUrl } from 'MediaManager/helpers/links'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getLivingLinks, upsertLink } from 'MediaManager/services/links'
import { getManageProperties, getPropertyById } from 'MediaManager/services/property'
import { getLivingVariants } from 'MediaManager/services/variants'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import Checkbox from 'shared/components/Checkbox'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import { useRequest } from 'shared/hooks/useRequest'
import SightmapIntegration from '../SightmapIntegration'
import { toast } from 'react-toastify'

const options = [
  {
    label: 'Exit Intent',
    name: 'exit_intent',
  },
  {
    label: 'As Staging',
    name: 'as_staging',
  },
  {
    label: 'Hide vendor',
    name: 'hide_vendor',
  },
  {
    label: 'Hide prices',
    name: 'hide_prices',
  },
  {
    label: 'Hide disclaimer',
    name: 'hide_disclaimer',
  },
  {
    label: 'Single line',
    name: 'single_line',
  },
   {
    label: 'Show endless guide animation',
    name: 'show_endless_animation',
  },
  {
    label: 'Debug',
    name: 'debug',
  },
]

function EditLivingLinksScreen({}) {
  const { property_id, id } = useSearch()

  const [editLink, setEditLink] = useState(false)

  useEffect(() => {
    if (id) {
      ;(async () => {
        const { data } = await getLivingLinks()
        const link = data.long_short_links.find((link) => link.id === id)
        setEditLink(data.long_short_links.find((link) => link.id === id))
        const vals = {
          ...link,
          ...link.configuration,
        }
        formik.setValues({ vals })
      })()
    }
  }, [id])

  const getProperties = useCallback(async () => {
    const { data } = await getManageProperties()
    return data.properties
  }, [])

  const { data: properties, loading: propertiesLoading } = useRequest(getProperties)

  const propertiesOptions = useMemo(() => {
    if (!properties) {
      return []
    }
    return (
      properties
        // .sort((a, b) => a.property_name.localeCompare(b.property_name))
        .map(({ id, property_name }) => ({ value: id, label: property_name }))
    )
  }, [properties])

  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      property: Number(property_id),
    },
    validateOnChange: false,
    validateOnBlur: true,
    // validationSchema,
    // validateOnMount:true,
    onSubmit: async (values) => {
      const payload = {
        link: url,
        description: values.description,
        configuration: {
          ...formik.values,
          property_name: properties.find((property) => property.id === formik.values.property)?.property_name,
          property_id: properties.find((property) => property.id === formik.values.property)?.id,
          unit_type_id: values.line,
        },
      }

      try {
        const { data } = await upsertLink({
          id: editLink ? editLink.id : undefined,
          ...payload,
        })
        if (!editLink) {
          setEditLink({
            ...data.upsert_long_short_links[0],
            ...payload,
          })
        }
        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      }
    },
  })

  useEffect(() => {
    if (editLink) {
      formik.setValues({
        ...editLink,
        ...editLink.configuration,
      })
    }
  }, [editLink])

  const [url, setUrl] = useState()

  const getProperty = useCallback(async () => {
    if (!properties || !formik.values.property) {
      return null
    }
    const { data } = await getPropertyById(formik.values.property)
    return data.properties_by_id
  }, [formik.values.property, properties])

  const { data: property, loading: linesLoading } = useRequest(getProperty)

  const lineOptions = useMemo(() => {
    if (!property) {
      return []
    }
    return property.unit_types.map(({ id, name }) => ({ value: id, label: name }))
  }, [property])

  const getVariants = useCallback(async () => {
    const { data } = await getLivingVariants()
    return data.living_variants
  }, [])

  const { data: variants, loading: variantsLoading } = useRequest(getVariants)

  const variantsOptions = useMemo(() => {
    if (!variants) {
      return []
    }
    return variants.map(({ id, living_variant_type }) => ({ value: id, label: living_variant_type.type }))
  }, [variants])

  const appVariantParamOption = useMemo(() => {
    const appVariantParams = [
      {
        label: 'None',
        value: null,
      }, // Default variant
      {
        label: 'Default',
        value: 'df',
      }, // Default variant
      {
        label: 'Click to form',
        value: 'ctf',
      },
      {
        label: 'Living',
        value: 'sw',
      },
      {
        label: 'EW',
        value: 'ew',
      },
      {
        label: 'Property direct',
        value: 'pd',
      },
      {
        label: 'Offices',
        value: 'of',
      },
      {
        label: 'Brodsky',
        value: 'br',
      },
    ]
    return appVariantParams
  }, [variants])

  useEffect(() => {
    if (!variants) {
      return
    }

    const variantDomain = variants.find((v) => v.id === formik.values.variant)?.configuration?.domain

    if (!variantDomain && !formik.values.app_variant_param) {
      setUrl('Please select a variant or variant with domain')
      return
    }
    const baseUrl = getLivingBaseUrl(variantDomain, formik.values.app_variant_param)
    const params = {
      ...options.reduce((acc, option) => ({ ...acc, [option.name]: !!formik.values[option.name] }), {}),
      package_id: formik.values.package_id,
      property: formik.values.property,
      line_id: formik.values.line,
      bg_hex_color: formik.values.bg_hex_color,
    }

    const url = getLivingWidgetUrl(baseUrl, {
      ...Object.entries(params)
        .filter(([key, value]) => !!value)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    })
    setUrl(url)
  }, [formik.values, variants])

  const [resultValue, setResultValue] = useState(null)

  const isValid = useMemo(() => {
    return url?.includes('https://')
  }, [url])

  return (
    <ScreenLayout title={`${editLink ? 'Edit' : 'Create'} SparX Living Link`}>
      <>
        {resultValue && (
          <ActionSuccessModal
            action="Living link"
            state={resultValue}
            onClose={() => {
              setResultValue(null)
            }}
            skipConfirm
            skipBack
            // onConfirm={resetForm}
          />
        )}
        <form onSubmit={formik.handleSubmit} className="w-full">
          {propertiesLoading || linesLoading ? (
            <div>
              <Loader />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-[16px]">
                <Select
                  onChange={(val) => formik.setFieldValue('property', val.value)}
                  value={propertiesOptions.find((line) => line.value === formik.values.property) || ''}
                  options={propertiesOptions}
                  placeholder="Property name"
                />

                {property && (
                  <Select
                    onChange={(val) => formik.setFieldValue('line', val.value)}
                    value={lineOptions.find((line) => line.value === formik.values.line) || ''}
                    options={lineOptions}
                    placeholder="Line"
                  />
                )}
                {variants && (
                  <Select
                    onChange={(val) => formik.setFieldValue('variant', val.value)}
                    value={variantsOptions.find((variant) => variant.value === formik.values.variant) || ''}
                    options={variantsOptions}
                    placeholder="Variants"
                  />
                )}
              </div>
              <div className="grid grid-cols-4 gap-[16px] mt-[16px]">
                <Input
                  label="Description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder="Description"
                />
                <Input
                  label="Default Package (optional)"
                  name="package_id"
                  value={formik.values.package_id}
                  onChange={formik.handleChange}
                  placeholder="Value"
                />
                <Input
                  label="Default Hex Color (optional)"
                  name="bg_hex_color"
                  value={formik.values.bg_hex_color}
                  onChange={formik.handleChange}
                  placeholder="Value"
                />
                <Select
                  onChange={(val) => formik.setFieldValue('app_variant_param', val.value)}
                  value={
                    appVariantParamOption.find((variant) => variant.value === formik.values.app_variant_param) || ''
                  }
                  options={appVariantParamOption}
                  placeholder="App Variant Param"
                  label="App Variant Param (optional)"
                />
              </div>
              <div className="mt-[16px]">
                <div className="text-[14px] opacity-50">Overruling Flags</div>
                <div className="grid grid-cols-3 gap-[16px] border-[1px] rounded-[8px] p-[16px] mt-[16px]">
                  {options.map((option) => (
                    <div className="flex items-center ">
                      <Checkbox
                        name={option.name}
                        checked={formik.values[option.name]}
                        onChange={() => {
                          formik.setFieldValue(option.name, !formik.values[option.name])
                        }}
                      />
                      <div className="ml-3 truncate">{option.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-[16px]">
                <Input label="Short URL" placeholder="-" disabled value={editLink.link_short} />
              </div>
              <div className="mt-[16px]">
                <Input label="Full URL" disabled value={url} placeholder="Value" />
              </div>
              <div className="flex justify-end items-center mt-[16px]">
                <Button rounded variant="secondary" onClick={() => window.history.back()}>
                  Cancel
                </Button>
                <SightmapIntegration
                  link={editLink}
                  onCreated={(data) => {
                    toast.success('Sightmap link created successfully', { theme: 'colored' })
                    setEditLink({
                      ...editLink,
                      ...data,
                    })
                  }}
                />
                <Button rounded type="submit" disabled={!isValid} className="ml-[16px]">
                  {id || editLink ? 'Update' : 'Create'} short url
                </Button>
              </div>
            </>
          )}
        </form>
      </>
    </ScreenLayout>
  )
}

export default EditLivingLinksScreen
